import React, { useEffect, useState } from 'react';
import { validatePublish, gelGetExecutionReadiness } from '../../services/api';

const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

/**
 * What still stands between this agent and running under protection.
 *
 * <h3>The verdict is the gate's, not this component's</h3>
 *
 * `PublishService.runPublishValidation` already decides each check's severity.
 * An unmet requirement is "error" when its gate is enforcing and "warning" when
 * it is not, and publish blocks on "error" alone. A surface that wrote its own
 * sentence about what is blocked would be right only for the mode its author
 * had in mind and quietly wrong in the other. This renders the decision, so it
 * stays correct however the platform is configured.
 *
 * <h3>What a check actually carries</h3>
 *
 * Every producer sends `check`, `passed` and `message`, built by its own private
 * helper (`PhaseGateService:964`, `ExternalAgentLifecycleService:474`,
 * `PublishService:743`). `message` is the sentence; it is always present and is
 * the line rendered. `severity` and `recommendation` come from the publish
 * validator alone, and `recommendation` only when that check has a next step
 * worth its own line. Nothing emits `remediation` — this component used to read
 * it, which read as a fallback and was dead code.
 *
 * <h3>Which gate, and why it depends on the archetype</h3>
 *
 * Execution Ready replaces Publish for an ExteLet, and the readiness endpoint
 * and `markExecutionReady` both call the same evaluator, so what is shown is
 * what is enforced. Reading the publish validator for an ExteLet would answer a
 * question nobody asked and miss every requirement that actually blocks it.
 *
 * <h3>No severity means blocking</h3>
 *
 * Readiness checks carry no severity because they are all blocking. Treating a
 * missing severity as "only flagged" would report a clear gate while every
 * check failed.
 *
 * <h3>A failed read is not a pass</h3>
 *
 * If the gate cannot be read, the component says so. Falling back to "nothing
 * is in the way" because a call fell over is the worst thing it could claim —
 * it would be reporting a clear gate nobody checked.
 */
/** A check with no severity is blocking: readiness sends none, and all of it blocks. */
const blocks = (c) => !c.passed && (c.severity === undefined || c.severity === 'error');

export default function GateChecks({ agentId, registrationId, archetype, onCleared }) {
  const [checks, setChecks] = useState(null);
  const [error, setError] = useState(null);

  const readiness = String(archetype || '').toLowerCase() === 'extelet';
  const subject = readiness ? registrationId : agentId;

  useEffect(() => {
    /* No subject, no read. Falling back to the other gate would show an answer
       to a different question. */
    if (!subject) return undefined;
    let alive = true;
    (async () => {
      try {
        const body = unwrap(readiness
          ? await gelGetExecutionReadiness(subject)
          : await validatePublish(subject));
        const list = Array.isArray(body?.checks) ? body.checks : [];
        if (alive) { setChecks(list); setError(null); }
      } catch (e) {
        if (alive) { setChecks(null); setError(e?.message || 'unknown'); }
      }
    })();
    return () => { alive = false; };
  }, [subject, readiness]);

  const unmet = (checks || []).filter((c) => !c.passed);
  const blocking = unmet.filter(blocks);
  const flagged = unmet.filter((c) => !blocks(c));

  /* Declared before the early returns below. Hooks run in call order, and a
     hook sitting after a `return` runs on some renders and not others — React
     then reads the wrong slot and throws. */
  const cleared = checks !== null && !error && blocking.length === 0;
  useEffect(() => {
    if (cleared && typeof onCleared === 'function') onCleared();
  }, [cleared, onCleared]);

  if (error) {
    return (
      <div className="p7-note p7-note--hold" aria-label="Gate checks">
        We could not read the gate for this agent just now. Nothing has changed — try again,
        or open it in the full platform.
      </div>
    );
  }
  if (checks === null) return null;

  return (
    <section aria-label="Gate checks" style={{ minWidth: 0 }}>
      <h2 className="p7-eyebrow" style={{ margin: '0 0 9px' }}>Before it can run</h2>
      <p style={{ margin: '0 0 var(--p7-space-4)', font: 'var(--p7-text-body)',
                  fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-muted)',
                  lineHeight: 1.6, maxWidth: '64ch' }}>
        {blocking.length === 0
          ? 'Nothing is in the way. Every requirement this agent has to meet is met.'
          : `${blocking.length} of these stops it from going live${
            flagged.length ? `, and ${flagged.length} more is worth clearing` : ''}.`}
      </p>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {checks.map((c) => {
          const stops = blocks(c);
          const tone = c.passed ? 'var(--p7-allow-ink)'
            : stops ? 'var(--p7-deny)' : 'var(--p7-hold-ink)';
          return (
            <li key={c.check}
                style={{ display: 'flex', gap: '10px', alignItems: 'flex-start',
                         padding: '9px 0', borderTop: '1px solid var(--p7-line-soft)' }}>
              <span aria-hidden="true" style={{ flex: 'none', color: tone, fontWeight: 700 }}>
                {c.passed ? '✓' : stops ? '✕' : '!'}
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', font: 'var(--p7-text-ui)',
                               fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink)' }}>
                  {c.message}
                </span>
                {!c.passed && c.recommendation ? (
                  <span style={{ display: 'block', marginTop: '2px',
                                 font: 'var(--p7-text-caption)',
                                 fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
                    {c.recommendation}
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
